import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar, AvatarImage, AvatarFallback } from '../avatar';

type AvatarProps = React.ComponentProps<typeof Avatar>;

const avatarLabelVariants = cva('rocco-avatar-label flex items-center gap-2', {
    variants: {
        orientation: {
            horizontal: 'flex-row',
            vertical: 'flex-col items-center',
        },
    },
    defaultVariants: {
        orientation: 'horizontal',
    },
});

const labelVariants = cva('rocco-avatar-label-text', {
    variants: {
        weight: {
            regular: 'font-normal',
            medium: 'font-medium',
            semibold: 'font-semibold',
        },
        size: {
            small: 'text-sm',
            medium: 'text-base',
            large: 'text-lg',
        },
    },
    defaultVariants: {
        weight: 'regular',
        size: 'medium',
    },
});

export interface AvatarLabelProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof avatarLabelVariants> {
    /**
     * The label text to display next to the avatar
     */
    label: string;
    /**
     * Optional secondary text to display below the main label
     */
    secondaryLabel?: string;
    /**
     * The font weight of the label
     */
    labelWeight?: VariantProps<typeof labelVariants>['weight'];
    /**
     * The image source for the avatar
     */
    avatarSrc?: string;
    /**
     * The fallback text to display when image fails to load
     */
    avatarFallback?: string;
    /**
     * The background color of the avatar
     */
    avatarBackgroundColor?: string;
    /**
     * The padding of the avatar
     */
    avatarPadding?: string;
    /**
     * The size of the avatar
     */
    avatarSize?: AvatarProps['size'];
}

/**
 * AvatarLabel is a component that displays an avatar and a label next to each other.
 * It is used to display the name of a professional or a user.
 */
export const AvatarLabel = React.forwardRef<HTMLDivElement, AvatarLabelProps>(
    (
        {
            label,
            secondaryLabel,
            orientation,
            labelWeight = 'regular',
            className,
            avatarSrc,
            avatarFallback,
            avatarBackgroundColor = 'white',
            avatarSize = 'square32',
            avatarPadding = '0px',
            ...props
        },
        ref,
    ) => (
        <div
            ref={ref}
            className={avatarLabelVariants({ orientation, className })}
            {...props}
        >
            <Avatar
                size={avatarSize}
                borderWidth="small"
                borderColor="light"
                style={{
                    backgroundColor: avatarBackgroundColor,
                }}
            >
                <AvatarImage
                    src={avatarSrc}
                    style={{ padding: avatarPadding }}
                />
                <AvatarFallback delayMs={1000}>{avatarFallback}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
                <span className={labelVariants({ weight: labelWeight })}>
                    {label}
                </span>
                {secondaryLabel && (
                    <span className="text-xs text-muted-foreground">
                        {secondaryLabel}
                    </span>
                )}
            </div>
        </div>
    ),
);

AvatarLabel.displayName = 'AvatarLabel';
